const SVG_NS = "http://www.w3.org/2000/svg";

function trimString(value) {
  return String(value ?? "").trim();
}
export function appendIcon(parent, href, className = "fishy-icon size-4 shrink-0 opacity-75") {
    const normalizedHref = trimString(href);
    if (!normalizedHref) {
      return;
    }
    const createSvgElement =
      typeof document.createElementNS === "function"
        ? (tagName) => document.createElementNS(SVG_NS, tagName)
        : (tagName) => document.createElement(tagName);
    const svg = createSvgElement("svg");
    svg.setAttribute("class", className);
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    const use = createSvgElement("use");
    use.setAttribute("width", "100%");
    use.setAttribute("height", "100%");
    use.setAttribute("href", normalizedHref);
    svg.appendChild(use);
    parent.appendChild(svg);
  }
  function selectedOptionForValue(options, value, emptyLabel) {
    const normalizedValue = trimString(value);
    return options.find((option) => option.value === normalizedValue)
      || (emptyLabel === undefined ? options[0] : null) || {
      value: normalizedValue,
      label: emptyLabel ?? normalizedValue,
      iconHref: "",
    };
  }

  function optionSearchText(option) {
    return [
      option.searchText,
      option.label,
      option.value,
    ]
      .map(trimString)
      .filter(Boolean)
      .join(" ");
  }


  function buildSearchableOptionContent(option) {
    const content = document.createElement("span");
    content.className = "flex min-w-0 max-w-full flex-1 items-center gap-3";
    appendIcon(content, option.iconHref, "fishy-icon size-5 shrink-0");

    const labelText = document.createElement("span");
    labelText.className = "block min-w-0 w-max flex-initial whitespace-normal break-words font-medium";
    labelText.textContent = option.label;
    content.appendChild(labelText);
    return content;
  }

  function renderSearchableOptionTemplate(option) {
    const template = document.createElement("template");
    template.dataset.role = "selected-content";
    template.setAttribute("data-value", option.value);
    template.setAttribute("data-label", option.label);
    template.setAttribute("data-search-text", optionSearchText(option));
    if (option.contentMarkup !== undefined) {
      // Markup must come from a trusted view renderer, never raw option data.
      template.innerHTML = option.contentMarkup;
    } else {
      template.content.appendChild(buildSearchableOptionContent(option));
    }
    return template;
  }

  function appendTemplateContent(parent, template) {
    parent.appendChild(template.content.cloneNode(true));
  }

export function renderSearchableSelector({
    id,
    label,
    value,
    options,
    onSelect,
    disabled = false,
    placeholder = "Search",
    emptyLabel,
    compact = false,
  }) {
    const selected = selectedOptionForValue(options, value, emptyLabel);
    const selectedPresentation = option => compact && option.compactLabel
      ? { ...option, label: option.compactLabel }
      : option;
    const panelId = `${id}-panel`;
    const searchId = `${id}-search`;
    const resultsId = `${id}-results`;
    const dropdownId = `${id}-selector`;

    const dropdown = document.createElement("fishy-searchable-dropdown");
    dropdown.id = dropdownId;
    dropdown.className = "relative block min-w-0 max-w-full";
    dropdown.dataset.role = "selector";
    dropdown.setAttribute("label", selected.label);
    dropdown.setAttribute("value", selected.value);
    dropdown.setAttribute("placeholder", placeholder);
    dropdown.setAttribute("panel-mode", "detached");
    if (!compact) dropdown.setAttribute("panel-placement", "overlay-anchor");
    dropdown.setAttribute("panel-min-width", "panel");
    dropdown.setAttribute("panel-width", "max-content");
    dropdown.addEventListener("change", (event) => {
      if (event.target !== dropdown) {
        return;
      }
      const next = trimString(dropdown.value);
      if (!disabled && next !== value) {
        Promise.resolve(onSelect(next)).catch((error) => {
          globalThis.reportError?.(error);
        });
      }
    });

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.dataset.role = "trigger";
    trigger.className = "flex min-h-10 w-full min-w-0 max-w-full items-center justify-between gap-3 rounded-box border border-base-300 bg-base-100 px-3 py-2 text-left text-sm shadow-sm hover:bg-base-200";
    if (compact) trigger.className = "flex min-h-8 w-fit items-center justify-between gap-2 rounded-box border border-base-300 bg-base-100 px-2 py-1 text-left text-sm hover:bg-base-200";
    trigger.setAttribute("aria-label", label);
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-controls", panelId);
    trigger.setAttribute("aria-disabled", String(disabled));
    trigger.disabled = disabled;

    const selectedContent = document.createElement("span");
    selectedContent.dataset.role = "selected-content";
    selectedContent.className = "flex min-w-0 flex-1 items-center gap-3 overflow-hidden text-sm";
    appendTemplateContent(selectedContent, renderSearchableOptionTemplate(selectedPresentation(selected)));
    trigger.appendChild(selectedContent);
    appendIcon(trigger, "#fishy-caret-down", "fishy-icon size-4 shrink-0 opacity-60");
    dropdown.appendChild(trigger);

    const panel = document.createElement("div");
    panel.id = panelId;
    panel.dataset.role = "panel";
    panel.className = "absolute left-0 top-0 z-50 w-full min-w-full max-w-full";
    panel.hidden = true;

    const panelShell = document.createElement("div");
    panelShell.className = "grid w-full min-w-full overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg";

    const searchLabel = document.createElement("label");
    searchLabel.className = "flex min-h-10 w-full min-w-full items-center gap-3 bg-base-100 px-3 py-2 text-sm";
    appendIcon(searchLabel, "#fishy-search-field", "fishy-icon size-4 shrink-0 opacity-60");

    const searchInput = document.createElement("input");
    searchInput.id = searchId;
    searchInput.dataset.role = "search-input";
    searchInput.type = "search";
    searchInput.className = "w-full border-0 bg-transparent p-0 shadow-none outline-none";
    searchInput.style.outline = "none";
    searchInput.style.boxShadow = "none";
    searchInput.setAttribute("aria-label", placeholder);
    searchInput.placeholder = placeholder;
    searchInput.autocomplete = "off";
    searchInput.spellcheck = false;
    searchLabel.appendChild(searchInput);
    panelShell.appendChild(searchLabel);

    const resultsFrame = document.createElement("div");
    resultsFrame.className = "px-1 pb-1";
    const results = document.createElement("ul");
    results.id = resultsId;
    results.tabIndex = -1;
    results.dataset.role = "results";
    results.className = "menu menu-vertical menu-sm max-h-72 w-full flex-nowrap gap-1 overflow-x-hidden overflow-y-auto p-1";
    const promptItem = document.createElement("li");
    promptItem.className = "menu-disabled";
    const promptLabel = document.createElement("span");
    promptLabel.className = "text-base-content/70";
    promptLabel.textContent = placeholder;
    promptItem.appendChild(promptLabel);
    results.appendChild(promptItem);
    resultsFrame.appendChild(results);
    panelShell.appendChild(resultsFrame);
    panel.appendChild(panelShell);
    dropdown.appendChild(panel);

    const catalog = document.createElement("div");
    catalog.dataset.role = "selected-content-catalog";
    catalog.hidden = true;
    options.forEach((option) => {
      catalog.appendChild(renderSearchableOptionTemplate(selectedPresentation(option)));
      if (compact) {
        const resultTemplate = renderSearchableOptionTemplate(option);
        resultTemplate.dataset.role = "option-content";
        catalog.appendChild(resultTemplate);
      }
    });
    dropdown.appendChild(catalog);

    return dropdown;
  }
